import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { MiniAudioPlayer } from './MiniAudioPlayer'
import {
  Play,
  Pause,
  SkipBack,
  SkipForward,
  RotateCcw,
  RotateCw,
  ChevronsUp,
  ChevronsDown,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'
import { useAudio } from './AudioContext'
import { NO_READING_PREFS, type ReadingPrefs } from '@/shared/lib/sectionTopics'
import { formatMinSec } from '@/shared/lib/formatDuration'
import { buildPlayableUnits } from './playableUnits'
import { fractionOfBar, segmentAtTime } from './scrubGeometry'
import type { Article } from './types'
/**
 * Die Hörleiste unter der Leseansicht: Abspielen, Spulen, Tempo, Sprung zum nächsten
 * Abschnitt. Das Audio-Element selbst gehört dem AudioContext, damit es beim Wechsel
 * der Seite weiterläuft und der Mini-Player es übernehmen kann.
 *
 * Drei Ansichten teilen sich den Player: die Ein-Artikel-Ansicht (Long-Kapitel), die
 * Ansicht mit allen Artikeln untereinander (Short-Kapitel) und die Vorschau der
 * Redaktion. Woran sie sich unterscheiden, steht an den Props.
 */
export function UserAudioPlayer({
  articles,
  initialSlug,
  visibleSlug,
  articleRefs,
  onSaveProgress,
  onArticleCompleted,
  onChapterCompleted,
  initialAutoPlay,
  chapterSlug,
  chapterTitle,
  initialAudioPosition,
  paywallVisible = false,
  embedded = false,
  activeArticleSlug = null,
  onActiveArticleChange,
  readingPrefs = NO_READING_PREFS,
  onBeyondPrev,
  onBeyondNext,
  listPosition = null,
}: {
  articles: Article[]
  initialSlug?: string
  visibleSlug: string | null
  articleRefs: React.MutableRefObject<Record<string, HTMLDivElement | null>>
  /**
   * Ein-Artikel-Ansicht (Long-Kapitel): Die Skip-Buttons wechseln dann den Artikel statt
   * die Audiodatei, und die Fortschrittsleiste zeigt nur dessen Abschnitte. Ohne diesen
   * Wert bleibt es beim kapitelweiten Verhalten (Short-Kapitel, Staff-Vorschau).
   */
  activeArticleSlug?: string | null
  onActiveArticleChange?: (slug: string) => void
  onSaveProgress?: (params: { articleSlug?: string; audioPosition?: number }) => void
  /** Feuert, wenn die letzte Audiodatei eines Artikels durchgelaufen ist. */
  onArticleCompleted?: (slug: string) => void
  onChapterCompleted?: () => void
  initialAutoPlay?: boolean
  chapterSlug?: string
  chapterTitle?: string | null
  initialAudioPosition?: number | null
  paywallVisible?: boolean
  /** Render docked to the bottom of the nearest positioned ancestor instead of portaling to a viewport-fixed bar (e.g. for embedding inside a preview panel). */
  embedded?: boolean
  /** Ausgeblendete Einleitung/Ende-Abschnitte fliegen auch aus der Playliste - sonst
   *  spielt Audio zu einer Karte, die gar nicht mehr angezeigt wird. Ohne Angabe (z.B.
   *  Staff-Vorschau) bleibt die volle Playliste erhalten. */
  readingPrefs?: ReadingPrefs
  /**
   * Blättern über das Kapitel hinaus - in die Liste, aus der man gekommen ist.
   *
   * Vorher saßen dafür zwei Pfeile im Titelbild. Sie waren an zwei verschiedenen Stellen
   * nachgebaut (Übersicht und Kurzkapitel) und blätterten nur durch Kapitel, nie durch
   * Artikel. Jetzt gibt es eine Zeile unter dem Player, und das Kapitelende ist für sie
   * kein Anschlag mehr: wo die Liste weitergeht, geht auch "Weiter".
   */
  onBeyondPrev?: () => void
  onBeyondNext?: () => void
  /** Zählt in der Liste statt im Kapitel - "3 von 12" meint dann die Zeile in "Meine Titel". */
  listPosition?: { index: number; total: number } | null
}) {
  const audioContext = useAudio()
  const audioRef = audioContext.audioRef
  const progressRef = useRef<HTMLDivElement>(null)
  const [speedIndex, setSpeedIndex] = useState(0)

  const articlesWithAudio = buildPlayableUnits(articles, readingPrefs)

  /**
   * Gehört die Datei, die der Context geladen hat, zu diesem Kapitel?
   *
   * Es gibt nur ein Audio-Element in der App. Liest man einen Artikel, während ein
   * anderes Kapitel noch läuft, bleibt das Element dem anderen: dieser Player zeigt
   * seinen Stand bei 0, fasst nichts an und schreibt nichts in den Context. Darüber
   * sitzt solange der Mini-Player mit dem laufenden Artikel. Erst ein Druck auf Play
   * (oder auf die Pfeile) nimmt das Element hier in Besitz - und beendet damit das
   * andere Kapitel.
   *
   * Die eingebettete Fassung (Vorschau der Redaktion) nimmt es sofort: dort gibt es
   * keinen Mini-Player, der das laufende Kapitel zeigen könnte - ein stummer Player
   * ohne jede Erklärung wäre schlimmer als der harte Wechsel.
   */
  const loadedUnitKey = audioContext.currentArticle?.unitKey
  const ownsAudio =
    embedded || !loadedUnitKey || articlesWithAudio.some((u) => u.unitKey === loadedUnitKey)

  // Use context's isPlaying directly to avoid sync issues
  const isPlaying = ownsAudio && audioContext.isPlaying
  // Beim Öffnen festgehalten: Pausiert sie das andere Kapitel über die angedockte Leiste,
  // soll die Leiste nicht unter ihrem Finger verschwinden.
  const [wasForeignAudioPlayingOnOpen] = useState(() => !ownsAudio && audioContext.isPlaying)

  const getInitialIndex = () => {
    // If context has a current unit from mini player, use that
    if (audioContext.currentArticle) {
      const index = articlesWithAudio.findIndex(
        (a) => a.unitKey === audioContext.currentArticle?.unitKey
      )
      if (index >= 0) return index
    }
    if (!initialSlug) return 0
    const index = articlesWithAudio.findIndex(
      (a) => a.slug.toLowerCase() === initialSlug.toLowerCase()
    )
    return index >= 0 ? index : 0
  }

  const [currentAudioIndex, setCurrentAudioIndex] = useState(getInitialIndex)
  // Dragging state: track visual position without changing the actual audio
  const [dragState, setDragState] = useState<{
    segmentIndex: number
    timeInSegment: number
  } | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const isDraggingRef = useRef(false)
  // Use drag position for display during drag, otherwise use context's currentTime.
  // Gehört die laufende Datei einem anderen Kapitel, ist deren Sekunde nicht unsere -
  // die Leiste steht dann am Anfang.
  const displayIndex = dragState !== null ? dragState.segmentIndex : currentAudioIndex
  const currentTime =
    dragState !== null ? dragState.timeInSegment : ownsAudio ? audioContext.currentTime : 0
  const [currentAudioDuration, setCurrentAudioDuration] = useState(0)
  const mountedWithPlayingAudio = useRef(isPlaying)

  // In der Ein-Artikel-Ansicht deckt die Leiste nur die Audios des sichtbaren Artikels ab.
  // `currentAudioIndex` zählt weiter über das ganze Kapitel - `unitOffset` rechnet um.
  const singleArticleMode = !!activeArticleSlug
  const scopedUnits = singleArticleMode
    ? articlesWithAudio.filter((u) => u.slug.toLowerCase() === activeArticleSlug!.toLowerCase())
    : articlesWithAudio
  const unitOffset = singleArticleMode
    ? Math.max(
        0,
        articlesWithAudio.findIndex(
          (u) => u.slug.toLowerCase() === activeArticleSlug!.toLowerCase()
        )
      )
    : 0

  // Calculate segment data based on article durations
  const segments = scopedUnits.map((a) => a.duration || 60)
  const totalDuration = segments.reduce((sum, d) => sum + d, 0)
  const segmentStarts = segments.reduce<number[]>((acc, d, i) => {
    if (i === 0) return [0]
    return [...acc, acc[i - 1] + segments[i - 1]]
  }, [])

  // Seek to initial audio position when mounting with saved progress (only once)
  // Skip if we mounted with already-playing audio (coming from mini player)
  const initialPositionHandled = useRef(mountedWithPlayingAudio.current)

  // Seek audio element when it loads and we have a pending position
  useEffect(() => {
    if (initialPositionHandled.current || !initialAudioPosition || initialAudioPosition <= 0) return
    // Gehört das Element noch dem anderen Kapitel, würde hier dessen Datei gespult.
    if (!ownsAudio) return
    const audio = audioRef.current
    if (!audio) return

    const seekToPosition = () => {
      initialPositionHandled.current = true
      audio.currentTime = initialAudioPosition
      audioContext.setCurrentTime(initialAudioPosition)
    }

    if (audio.readyState >= 1) {
      seekToPosition()
    } else {
      const handleCanPlay = () => {
        audio.removeEventListener('loadedmetadata', handleCanPlay)
        seekToPosition()
      }
      audio.addEventListener('loadedmetadata', handleCanPlay)
      return () => audio.removeEventListener('loadedmetadata', handleCanPlay)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialAudioPosition, audioContext.audioUrl])

  // Update currentAudioIndex when initialSlug changes (e.g., from async progress data loading)
  const initialSlugHandled = useRef(false)
  useEffect(() => {
    if (isPlaying || !initialSlug || initialSlugHandled.current) return
    if (!ownsAudio) return
    // Läuft das Audio bereits in diesem Artikel, ist der Startwunsch erledigt - sonst
    // könnte dieser Effekt in einer späteren Pause noch auf dessen erste Datei springen.
    const currentUnitSlug = articlesWithAudio[currentAudioIndex]?.slug
    if (currentUnitSlug && currentUnitSlug.toLowerCase() === initialSlug.toLowerCase()) {
      initialSlugHandled.current = true
      return
    }
    const index = articlesWithAudio.findIndex(
      (a) => a.slug.toLowerCase() === initialSlug.toLowerCase()
    )
    if (index >= 0 && index !== currentAudioIndex) {
      initialSlugHandled.current = true
      setCurrentAudioIndex(index)
      if (initialAudioPosition && initialAudioPosition > 0) {
        initialPositionHandled.current = false
        const seekWhenReady = () => {
          const audio = audioRef.current
          if (!audio) return
          if (audio.readyState >= 1) {
            audio.currentTime = initialAudioPosition
            audioContext.setCurrentTime(initialAudioPosition)
            initialPositionHandled.current = true
          } else {
            const handleLoaded = () => {
              audio.removeEventListener('loadedmetadata', handleLoaded)
              audio.currentTime = initialAudioPosition
              audioContext.setCurrentTime(initialAudioPosition)
              initialPositionHandled.current = true
            }
            audio.addEventListener('loadedmetadata', handleLoaded)
          }
        }
        setTimeout(seekWhenReady, 100)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSlug, articlesWithAudio, isPlaying, currentAudioIndex, initialAudioPosition])

  // Switch to visible article when paused (but not during dragging)
  //
  // Nur für die Ansicht mit allen Artikeln untereinander (Short-Kapitel): Dort scrollt
  // der Leser und das Audio zieht nach. In der Ein-Artikel-Ansicht ist es genau umgekehrt -
  // dort folgt die Ansicht dem Audio. Dieser Effekt würde dort gegen sie arbeiten: Beim
  // Artikelwechsel ziehen Audio-Index und sichtbarer Artikel in zwei Renders nach, und im
  // Render dazwischen würde er den Index auf die Einleitung des gerade beendeten Artikels
  // zurücksetzen - die Ansicht springt hin und her, das sichtbare Flackern.
  useEffect(() => {
    if (singleArticleMode) return
    if (isPlaying || isDraggingRef.current || !visibleSlug) return
    // Liegt das Audio schon im sichtbaren Artikel, ist nichts nachzuziehen - auch dann
    // nicht, wenn es in einem späteren Abschnitt davon steht. Ohne diese Bedingung lief
    // der Effekt nach jedem "vor" und "zurück" mit (der Dateiwechsel pausiert kurz, und
    // genau dann greift er) und zog den Index auf die erste Einheit des Artikels zurück
    // - der Sprung zum nächsten Abschnitt fiel dann einfach aus.
    if (articlesWithAudio[currentAudioIndex]?.slug === visibleSlug) return
    const index = articlesWithAudio.findIndex((a) => a.slug === visibleSlug)
    if (index >= 0 && index !== currentAudioIndex) {
      userChangedArticle.current = true
      setCurrentAudioIndex(index)
      if (!initialAudioPosition || initialAudioPosition <= 0 || initialPositionHandled.current) {
        audioContext.setCurrentTime(0)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    visibleSlug,
    isPlaying,
    articlesWithAudio,
    currentAudioIndex,
    initialAudioPosition,
    singleArticleMode,
  ])

  const speeds = [1, 1.2, 0.8]
  const playbackRate = speeds[speedIndex]

  useEffect(() => {
    if (!ownsAudio || !audioRef.current) return
    audioRef.current.playbackRate = playbackRate
  }, [ownsAudio, playbackRate, audioRef])

  // Track if the currentAudioIndex change came from context (auto-play) or user action
  const indexChangedFromContext = useRef(false)
  const prevContextUnitKeyRef = useRef<string | null>(null)

  // IMPORTANT: This effect must run BEFORE the forward sync effect below!
  // Sync local currentAudioIndex when context's currentArticle changes (e.g., from auto-play)
  useEffect(() => {
    if (!audioContext.currentArticle) return
    const contextUnitKey = audioContext.currentArticle.unitKey
    // Only process if context unit actually changed
    if (prevContextUnitKeyRef.current === contextUnitKey) return
    prevContextUnitKeyRef.current = contextUnitKey

    const newIndex = articlesWithAudio.findIndex((a) => a.unitKey === contextUnitKey)
    if (newIndex >= 0 && newIndex !== currentAudioIndex) {
      // Mark that this change came from context, so forward sync won't overwrite
      indexChangedFromContext.current = true
      userChangedArticle.current = true
      setCurrentAudioIndex(newIndex)
      // Nur innerhalb des sichtbaren Artikels scrollen. Wechselt die Automatik in den
      // nächsten Artikel, ist dessen Karte noch nicht gerendert - dort übernimmt die
      // Ansicht selbst und springt an den Anfang der Kopfkarte.
      const staysInActiveArticle =
        !singleArticleMode ||
        articlesWithAudio[newIndex]?.slug.toLowerCase() === activeArticleSlug!.toLowerCase()
      if (staysInActiveArticle) {
        articleRefs.current[contextUnitKey.toLowerCase()]?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioContext.currentArticle, articlesWithAudio, currentAudioIndex, activeArticleSlug])

  // Sync current article info with AudioContext for mini player
  // Only sync when user manually changes article, NOT when context changes (auto-play)
  const prevChapterSlugRef = useRef<string | null>(null)
  const prevChapterTitleRef = useRef<string | null>(null)

  useEffect(() => {
    // Skip if this change came from the context (auto-play)
    if (indexChangedFromContext.current) {
      indexChangedFromContext.current = false
      return
    }
    // Das Element gehört dem anderen Kapitel - erst ein Druck auf Play holt es hierher
    // (siehe `claimAudio`). Bis dahin bleibt der Context unberührt, sonst risse das
    // Einhängen dieser Ansicht dem Mini-Player die laufende Datei weg.
    if (!ownsAudio) return

    const currentArticle = articlesWithAudio[currentAudioIndex]
    if (currentArticle && currentArticle.audio) {
      // Only set if context doesn't already have this unit
      if (audioContext.currentArticle?.unitKey !== currentArticle.unitKey) {
        audioContext.setCurrentArticle({
          slug: currentArticle.slug,
          unitKey: currentArticle.unitKey,
          title: currentArticle.title,
          audio: currentArticle.audio,
          duration: currentArticle.duration,
          article_position: currentArticle.article_position,
        })
      }
    }
    if (chapterSlug && prevChapterSlugRef.current !== chapterSlug) {
      prevChapterSlugRef.current = chapterSlug
      audioContext.setChapterSlug(chapterSlug)
    }
    if (chapterTitle && prevChapterTitleRef.current !== chapterTitle) {
      prevChapterTitleRef.current = chapterTitle
      audioContext.setChapterTitle(chapterTitle)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAudioIndex, articlesWithAudio, chapterSlug, chapterTitle])

  // Sync articles list with AudioContext for mini player auto-play
  const prevUnitsKeyRef = useRef<string>('')
  useEffect(() => {
    if (!ownsAudio) return
    // Only sync if units actually changed (compare by unit keys)
    const unitsKey = articlesWithAudio.map((a) => a.unitKey).join(',')
    if (prevUnitsKeyRef.current === unitsKey) return
    prevUnitsKeyRef.current = unitsKey

    const audioArticles = articlesWithAudio.map((a) => ({
      slug: a.slug,
      unitKey: a.unitKey,
      title: a.title,
      audio: a.audio!,
      duration: a.duration,
      article_position: a.article_position,
    }))
    audioContext.setArticlesWithAudio(audioArticles)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articlesWithAudio])

  // Läuft das Audio ans Ende eines Artikels, zieht die Ein-Artikel-Ansicht nach. Der
  // Ref-Vergleich stellt sicher, dass nur ein echter Audiowechsel das auslöst - sonst
  // würde ein manueller Artikelwechsel sofort wieder auf den laufenden Artikel springen.
  const prevSyncedUnitIndex = useRef(currentAudioIndex)
  useEffect(() => {
    if (prevSyncedUnitIndex.current === currentAudioIndex) return
    prevSyncedUnitIndex.current = currentAudioIndex
    if (!onActiveArticleChange || !activeArticleSlug) return
    const unit = articlesWithAudio[currentAudioIndex]
    if (unit && unit.slug.toLowerCase() !== activeArticleSlug.toLowerCase()) {
      onActiveArticleChange(unit.slug)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAudioIndex, articlesWithAudio, activeArticleSlug])

  // Track if user has manually changed articles (vs initial load)
  const userChangedArticle = useRef(false)
  const prevResetIndexRef = useRef<number | null>(null)

  useEffect(() => {
    if (!ownsAudio) return
    // Don't reset time if we mounted with playing audio (coming from mini player)
    if (mountedWithPlayingAudio.current) {
      mountedWithPlayingAudio.current = false
      prevResetIndexRef.current = currentAudioIndex
      if (audioRef.current) {
        audioContext.setCurrentTime(audioRef.current.currentTime)
        setCurrentAudioDuration(audioRef.current.duration || 0)
      }
      return
    }
    // Only reset if index actually changed
    if (prevResetIndexRef.current === currentAudioIndex) {
      return
    }
    prevResetIndexRef.current = currentAudioIndex
    // Don't reset time if we have an initial audio position (initial load with saved progress)
    if (initialAudioPosition && initialAudioPosition > 0 && !userChangedArticle.current) {
      return
    }
    audioContext.setCurrentTime(0)
    setCurrentAudioDuration(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAudioIndex, initialAudioPosition])

  // Initial auto-play when navigating from previous chapter
  const initialAutoPlayHandled = useRef(false)
  useEffect(() => {
    if (!initialAutoPlay || initialAutoPlayHandled.current) return
    initialAutoPlayHandled.current = true
    // "autoplay=true" ist ein ausdrücklicher Wunsch - der holt das Element auch dann
    // hierher, wenn noch das andere Kapitel läuft.
    if (!ownsAudio) {
      claimAudio(currentAudioIndex, 0, true)
      return
    }
    audioContext.setAutoPlayOnLoad()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialAutoPlay])

  // Refs to hold latest values for use in event handlers
  const onSaveProgressRef = useRef(onSaveProgress)
  const onChapterCompletedRef = useRef(onChapterCompleted)
  const onArticleCompletedRef = useRef(onArticleCompleted)
  const visibleSlugRef = useRef(visibleSlug)
  const currentArticleSlugRef = useRef<string | undefined>(
    articlesWithAudio[currentAudioIndex]?.slug
  )

  useEffect(() => {
    onSaveProgressRef.current = onSaveProgress
  }, [onSaveProgress])
  useEffect(() => {
    onChapterCompletedRef.current = onChapterCompleted
  }, [onChapterCompleted])
  useEffect(() => {
    onArticleCompletedRef.current = onArticleCompleted
  }, [onArticleCompleted])
  useEffect(() => {
    visibleSlugRef.current = visibleSlug
  }, [visibleSlug])
  useEffect(() => {
    currentArticleSlugRef.current = articlesWithAudio[currentAudioIndex]?.slug
  }, [currentAudioIndex, articlesWithAudio])

  useEffect(() => {
    const audio = audioRef.current
    // Die Dauer, der gespeicherte Stand und "Artikel geschafft" gelten nur für die
    // eigene Datei. Ohne diese Bedingung haette das Ende des anderen Kapitels hier
    // einen Artikel als gelesen gezaehlt.
    if (!audio || !ownsAudio) return

    const handleLoadedMetadata = () => {
      setCurrentAudioDuration(audio.duration)
    }

    const handlePause = () => {
      if (onSaveProgressRef.current) {
        onSaveProgressRef.current({
          articleSlug: currentArticleSlugRef.current || visibleSlugRef.current || undefined,
          audioPosition: audio.currentTime || 0,
        })
      }
    }

    const handleEnded = () => {
      // Die gerade beendete Einheit war die letzte ihres Artikels, wenn die nächste
      // zu einem anderen Artikel gehört - oder gar keine mehr folgt.
      const endedUnit = articlesWithAudio[currentAudioIndex]
      const nextUnit = articlesWithAudio[currentAudioIndex + 1]
      if (endedUnit && nextUnit?.slug !== endedUnit.slug) {
        onArticleCompletedRef.current?.(endedUnit.slug)
      }

      const isLastAudio = currentAudioIndex >= articlesWithAudio.length - 1
      if (isLastAudio && onChapterCompletedRef.current) {
        onChapterCompletedRef.current()
      }
    }

    audio.addEventListener('loadedmetadata', handleLoadedMetadata)
    audio.addEventListener('pause', handlePause)
    audio.addEventListener('ended', handleEnded)

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata)
      audio.removeEventListener('pause', handlePause)
      audio.removeEventListener('ended', handleEnded)
    }
  }, [currentAudioIndex, articlesWithAudio, audioRef, ownsAudio])

  // Calculate progress percentage (use displayIndex for visual during drag)
  const scopedDisplayIndex = Math.min(
    Math.max(displayIndex - unitOffset, 0),
    Math.max(segments.length - 1, 0)
  )
  // Beim Artikelwechsel ziehen Audio-Index und sichtbarer Artikel in zwei Renders nach.
  // Solange sie auseinanderliegen, würde die Leiste ans Ende schnellen - dann lieber 0.
  const displayUnitInActiveArticle =
    !singleArticleMode ||
    articlesWithAudio[displayIndex]?.slug.toLowerCase() === activeArticleSlug!.toLowerCase()
  const chapterCurrentTime = displayUnitInActiveArticle
    ? (segmentStarts[scopedDisplayIndex] ?? 0) + currentTime
    : 0
  const chapterProgress = totalDuration > 0 ? (chapterCurrentTime / totalDuration) * 100 : 0

  const seekToPosition = (clientX: number) => {
    if (!progressRef.current || !audioRef.current) return
    const rect = progressRef.current.getBoundingClientRect()
    const targetChapterTime = fractionOfBar(clientX, rect) * totalDuration

    const scoped = segmentAtTime(segments, targetChapterTime)
    if (!scoped) return

    const targetIndex = scoped.index + unitOffset
    const timeWithinSegment = scoped.timeWithin

    if (targetIndex !== currentAudioIndex) {
      // Die Stelle geht als Vorgabe mit, statt sie nach einer gesetzten Frist
      // nachzuschieben - lud die Datei langsamer, lief sie vorher von der falschen
      // Stelle los.
      jumpToUnit(targetIndex, Math.max(0, timeWithinSegment), isPlaying)
    } else if (ownsAudio) {
      audioRef.current.currentTime = Math.max(0, timeWithinSegment)
    } else {
      // Noch läuft das andere Kapitel: die gezogene Stelle wird der Startpunkt, los
      // geht es mit dem Play-Knopf.
      claimAudio(currentAudioIndex, Math.max(0, timeWithinSegment), false)
    }
    // Context's timeupdate handler will update currentTime automatically
  }

  /**
   * Der Zieh-Effekt unten lauscht am Fenster, solange ein Finger auf der Leiste liegt.
   * Er braucht beim Loslassen die aktuelle Fassung von `seekToPosition` - die Funktion
   * entsteht aber bei jedem Rendern neu. Ueber die Ref bleibt der Effekt stabil und
   * greift trotzdem immer auf den neuesten Stand zu; stuende sie in der Abhaengigkeits-
   * liste, haengte und loeste der Effekt seine Listener bei jedem Rendern neu.
   */
  const seekToPositionRef = useRef(seekToPosition)
  seekToPositionRef.current = seekToPosition

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    isDraggingRef.current = true
    setIsDragging(true)
    seekToPosition(e.clientX)
  }

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    e.preventDefault()
    isDraggingRef.current = true
    setIsDragging(true)
    seekToPosition(e.touches[0].clientX)
  }

  useEffect(() => {
    if (!isDragging) return

    const handleMove = (clientX: number) => {
      if (!progressRef.current) return
      const rect = progressRef.current.getBoundingClientRect()
      const target = segmentAtTime(segments, fractionOfBar(clientX, rect) * totalDuration)
      if (!target) return
      // segmentIndex zählt wie currentAudioIndex über das ganze Kapitel; das Audio
      // bleibt unberührt, hier bewegt sich nur die Anzeige.
      setDragState({ segmentIndex: target.index + unitOffset, timeInSegment: target.timeWithin })
    }

    const handleEnd = (clientX: number) => {
      // Apply the drag position to actually change the audio
      seekToPositionRef.current(clientX)
      // Clear drag state
      setDragState(null)
      isDraggingRef.current = false
      setIsDragging(false)
    }

    const handleMouseMove = (e: MouseEvent) => handleMove(e.clientX)
    const handleMouseUp = (e: MouseEvent) => handleEnd(e.clientX)
    const handleTouchMove = (e: TouchEvent) => {
      e.preventDefault()
      handleMove(e.touches[0].clientX)
    }
    const handleTouchEnd = (e: TouchEvent) => {
      const touch = e.changedTouches[0]
      handleEnd(touch.clientX)
    }

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)
    window.addEventListener('touchmove', handleTouchMove, { passive: false })
    window.addEventListener('touchend', handleTouchEnd)

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
      window.removeEventListener('touchmove', handleTouchMove)
      window.removeEventListener('touchend', handleTouchEnd)
    }
  }, [isDragging, totalDuration, segments, unitOffset])

  if (articlesWithAudio.length === 0) return null

  const currentArticle = articlesWithAudio[currentAudioIndex]

  // Die Pfeile laufen immer über die einzelnen Audiodateien - erst durch die Abschnitte
  // des Artikels, dann in den nächsten Artikel. Den Artikelsprung machen die Buttons
  // unter dem Player, die über die Artikelliste navigieren (auch ohne Audio).
  const hasPrevAudio = currentAudioIndex > 0
  const hasNextAudio = currentAudioIndex < articlesWithAudio.length - 1
  // Im ersten Abschnitt gibt es keinen vorherigen, der Knopf hat aber trotzdem etwas zu
  // tun: an den Anfang des laufenden zurück. Nur bei 0:00 ganz vorn ist er wirklich ohne
  // Wirkung.
  const canGoBack = hasPrevAudio || currentTime > 3

  const activeArticleIndex = singleArticleMode
    ? articles.findIndex((a) => a.slug.toLowerCase() === activeArticleSlug!.toLowerCase())
    : -1
  const hasPrevArticle = activeArticleIndex > 0 || !!onBeyondPrev
  const hasNextArticle =
    (activeArticleIndex >= 0 && activeArticleIndex < articles.length - 1) || !!onBeyondNext
  // Ohne die Zeile säßen die Steuerelemente sehr dicht am unteren Rand - der
  // Ersatz-Abstand hält beide Varianten gleich hoch. Ein Kurzkapitel hat nur einen
  // Artikel und bekam die Zeile deshalb früher nie; führt die Liste weiter, bekommt es
  // sie jetzt.
  const showArticleNav =
    singleArticleMode && (articles.length > 1 || !!onBeyondPrev || !!onBeyondNext)
  const isOutOfSync =
    isPlaying && visibleSlug && currentArticle.slug.toLowerCase() !== visibleSlug.toLowerCase()
  // Die Vorschau der Redaktion hat keinen Mini-Player, und hinter der Bezahlschranke
  // soll nichts bedienbar sein. Nur wenn das andere Audio beim Öffnen lief (oder seither
  // wieder läuft): Pausiert hängt die Datei zwar noch im Element, aber niemand hört sie -
  // die Leiste stünde dann bei jedem Artikel da.
  const showForeignAudioBar =
    !ownsAudio &&
    (wasForeignAudioPlayingOnOpen || audioContext.isPlaying) &&
    !embedded &&
    !paywallVisible

  const visibleArticle = articles.find((a) => a.slug.toLowerCase() === visibleSlug?.toLowerCase())
  const audioIsAbove =
    isOutOfSync &&
    visibleArticle &&
    currentArticle.article_position < (visibleArticle.article_position ?? 0)

  /**
   * Holt das Audio-Element zu diesem Kapitel.
   *
   * Es gibt nur eines: mit dem Wechsel der Quelle endet das Kapitel, das der
   * Mini-Player zeigte, und der Mini-Player verschwindet mit ihm. Der Context bekommt
   * zuerst Startposition und Abspielwunsch, dann die neue Playliste und zuletzt die
   * Einheit - denn erst sie wechselt die Quelle.
   */
  const claimAudio = (index: number, startAt: number, play: boolean) => {
    const unit = articlesWithAudio[index]
    if (!unit) return
    audioContext.seekOnLoad(startAt)
    if (play) audioContext.setAutoPlayOnLoad()
    audioContext.setArticlesWithAudio(articlesWithAudio)
    if (chapterSlug) audioContext.setChapterSlug(chapterSlug)
    if (chapterTitle) audioContext.setChapterTitle(chapterTitle)
    audioContext.setCurrentArticle(unit)
    audioContext.setShowMiniPlayer(false)
  }

  const togglePlay = () => {
    // Läuft noch das andere Kapitel, beginnt dieses hier - an der gespeicherten Stelle,
    // wenn es eine gibt, sonst vorn.
    if (!ownsAudio) {
      const resumeAt =
        initialAudioPosition && !initialPositionHandled.current ? initialAudioPosition : 0
      claimAudio(currentAudioIndex, resumeAt, true)
      return
    }
    const audio = audioRef.current
    if (!audio) return
    // Use actual audio state instead of local isPlaying to avoid sync issues
    if (audio.paused) {
      audio.play().catch((err) => console.error('Audio play failed:', audio.currentSrc, err))
    } else {
      audio.pause()
    }
  }

  // Die beiden Spulknöpfe wirken auf die laufende Datei. Gehört sie dem anderen
  // Kapitel, gibt es hier noch nichts zu spulen - der Play-Knopf fängt erst an.
  const skipBack10 = () => {
    if (!ownsAudio || !audioRef.current) return
    audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime - 10)
  }

  const skipForward10 = () => {
    if (!ownsAudio || !audioRef.current) return
    const duration = audioRef.current.duration || currentAudioDuration
    audioRef.current.currentTime = Math.min(duration, audioRef.current.currentTime + 10)
  }

  const scrollToArticle = (unitKey: string) => {
    const el = articleRefs.current[unitKey.toLowerCase()]
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  /**
   * Springt auf eine andere Einheit und gibt deren Startposition ausdrücklich vor.
   *
   * Ohne diese Vorgabe würde die Datei dort einsetzen, wo sie in dieser Sitzung
   * zuletzt verlassen wurde (siehe `audioStartPosition`) - der Vor-Knopf landete
   * mitten im nächsten Abschnitt statt an seinem Anfang.
   *
   * Teilen zwei Einheiten dieselbe Audiodatei, wechselt die Quelle nicht und es kommt
   * kein `loadedmetadata`: dann wird hier sofort gespult, sonst bliebe die Vorgabe
   * liegen und würde die nächste geladene Datei verstellen.
   */
  const jumpToUnit = (index: number, startAt: number, autoPlay: boolean) => {
    const target = articlesWithAudio[index]
    if (!target) return
    const audio = audioRef.current
    const sameFile =
      ownsAudio && !!audio && articlesWithAudio[currentAudioIndex]?.audio === target.audio

    userChangedArticle.current = true
    setCurrentAudioIndex(index)
    scrollToArticle(target.unitKey)

    // Läuft noch das andere Kapitel, holt der Sprung das Element hierher.
    if (!ownsAudio) {
      claimAudio(index, startAt, autoPlay)
      if (onSaveProgress) {
        onSaveProgress({ articleSlug: target.slug, audioPosition: startAt })
      }
      return
    }

    if (sameFile) {
      audio.currentTime = startAt
      audioContext.setCurrentTime(startAt)
      if (autoPlay) {
        audio.play().catch((err) => console.error('Audio play failed:', audio.currentSrc, err))
      }
    } else {
      audioContext.seekOnLoad(startAt)
      if (autoPlay) audioContext.setAutoPlayOnLoad()
    }

    if (onSaveProgress) {
      onSaveProgress({ articleSlug: target.slug, audioPosition: startAt })
    }
  }

  /**
   * Wechselt in der Ein-Artikel-Ansicht zum Nachbarartikel und startet dessen erste
   * Audiodatei. Artikel ohne Audio werden trotzdem angezeigt - dann läuft nichts weiter.
   */
  const goToArticle = (offset: -1 | 1) => {
    const target = articles[activeArticleIndex + offset]
    if (!target) {
      // Das Kapitel ist zu Ende, die Liste aber nicht.
      if (offset === -1) onBeyondPrev?.()
      else onBeyondNext?.()
      return
    }
    userChangedArticle.current = true
    onActiveArticleChange?.(target.slug)

    const firstUnitIndex = articlesWithAudio.findIndex(
      (u) => u.slug.toLowerCase() === target.slug.toLowerCase()
    )
    if (firstUnitIndex >= 0) {
      // Nur weiterspielen, wenn gerade gespielt wird - beim stillen Durchblättern
      // (und in der Vorschau der Redaktion) soll der Wechsel kein Audio starten.
      jumpToUnit(firstUnitIndex, 0, isPlaying)
    } else if (onSaveProgress) {
      onSaveProgress({ articleSlug: target.slug, audioPosition: 0 })
    }
  }

  /**
   * Zurück heißt zuerst "an den Anfang des laufenden Abschnitts" und erst beim zweiten
   * Druck "an den Anfang des vorherigen" - wie man es von Musik-Apps kennt. Die drei
   * Sekunden sind die Schwelle dafür.
   */
  const goToPrevAudio = () => {
    const audio = audioRef.current
    if (audio && audio.currentTime > 3) {
      audio.currentTime = 0
      audioContext.setCurrentTime(0)
      if (onSaveProgress) {
        onSaveProgress({ articleSlug: currentArticle?.slug, audioPosition: 0 })
      }
      return
    }
    if (hasPrevAudio) jumpToUnit(currentAudioIndex - 1, 0, true)
  }

  /** Vor heißt immer: Anfang des nächsten Abschnitts, und er läuft. */
  const goToNextAudio = () => {
    if (hasNextAudio) jumpToUnit(currentAudioIndex + 1, 0, true)
  }

  const cycleSpeed = () => {
    setSpeedIndex((speedIndex + 1) % speeds.length)
  }

  const saveCurrentProgress = () => {
    if (!onSaveProgress) return
    const audio = audioRef.current
    onSaveProgress({
      articleSlug: currentArticle?.slug || visibleSlug || undefined,
      audioPosition: audio?.currentTime || 0,
    })
  }

  // Calculate segment boundaries as percentages for visual dividers
  const segmentBoundaries = segmentStarts.slice(1).map((start) => (start / totalDuration) * 100)

  const playerContent = (
    <>
      {isOutOfSync && !paywallVisible && (
        <button
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            saveCurrentProgress()
            scrollToArticle(currentArticle.unitKey)
          }}
          onTouchEnd={(e) => {
            e.preventDefault()
            e.stopPropagation()
            saveCurrentProgress()
            scrollToArticle(currentArticle.unitKey)
          }}
          className={
            embedded
              ? 'absolute bottom-24 right-4 p-3 rounded-full bg-white dark:bg-card border border-border shadow-lg z-40 touch-manipulation hover:bg-gray-50 dark:hover:bg-card/80'
              : 'fixed bottom-36 right-4 p-3 rounded-full bg-white dark:bg-card border border-border shadow-lg z-[9999] touch-manipulation hover:bg-gray-50 dark:hover:bg-card/80'
          }
          title="Zum aktuellen Audio scrollen"
        >
          {audioIsAbove ? (
            <ChevronsUp className="w-5 h-5 text-primary" />
          ) : (
            <ChevronsDown className="w-5 h-5 text-primary" />
          )}
        </button>
      )}
      <div
        className={
          embedded
            ? 'absolute bottom-0 left-0 right-0 z-40 transition-transform duration-500'
            : 'fixed bottom-0 left-0 right-0 z-[9999] transition-transform duration-500'
        }
        style={{ transform: paywallVisible ? 'translateY(100%)' : 'translateY(0)' }}
      >
        {/* Das andere Kapitel laeuft weiter und bleibt sichtbar - mit seiner eigenen
            Leiste ueber dieser. Ein Druck auf Play hier holt das Audio herueber, und
            der Mini-Player verschwindet. */}
        {showForeignAudioBar && (
          <div className="px-4 pb-2">
            <MiniAudioPlayer docked />
          </div>
        )}
        <div className="bg-white dark:bg-[hsl(356,21%,38%)] border-t border-border dark:border-white/10 shadow-lg rounded-t-2xl">
          <div className="px-4 pt-4">
            <div
              ref={progressRef}
              className="relative py-2 cursor-pointer"
              style={{ touchAction: 'none' }}
              onMouseDown={handleMouseDown}
              onTouchStart={handleTouchStart}
            >
              <div className="relative h-1 bg-gray-200 dark:bg-white/20 rounded-full">
                {/* Segment dividers */}
                {segmentBoundaries.map((pos, i) => (
                  <div
                    key={i}
                    className="absolute top-0 w-0.5 h-full bg-white dark:bg-card z-10"
                    style={{ left: `${pos}%` }}
                  />
                ))}
                {/* Progress fill */}
                <div
                  className="absolute h-full bg-secondary-foreground dark:bg-primary/70 rounded-full"
                  style={{ width: `${chapterProgress}%` }}
                />
                {/* Handler */}
                <div
                  className="absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 bg-secondary-foreground dark:bg-primary rounded-full shadow-sm z-20"
                  style={{ left: `calc(${chapterProgress}% - 5px)` }}
                />
              </div>
            </div>
            <div className="flex justify-between text-xs text-gray-500 dark:text-white/75 mt-1">
              <span>{formatMinSec(chapterCurrentTime)}</span>
              <span>{formatMinSec(totalDuration)}</span>
            </div>
          </div>

          {/* Audio element is rendered by AudioContext */}

          <div
            className={`grid grid-cols-3 items-center px-4 pt-1 ${showArticleNav ? 'pb-8' : 'pb-12'} relative z-10`}
          >
            <div className="pointer-events-none"></div>

            <div className="flex items-center justify-center gap-4">
              <button
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  goToPrevAudio()
                }}
                onTouchEnd={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  goToPrevAudio()
                }}
                disabled={!canGoBack}
                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-transparent relative z-20 touch-manipulation"
                title="Vorheriges Audio"
              >
                <SkipBack className="w-5 h-5 text-primary dark:text-white" fill="currentColor" />
              </button>

              <button
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  skipBack10()
                  saveCurrentProgress()
                }}
                onTouchEnd={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  skipBack10()
                  saveCurrentProgress()
                }}
                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 relative z-20 touch-manipulation"
                title="10 Sekunden zurück"
              >
                <RotateCcw className="w-5 h-5 text-primary dark:text-white" />
              </button>

              <button
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  saveCurrentProgress()
                  togglePlay()
                }}
                onTouchEnd={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  saveCurrentProgress()
                  togglePlay()
                }}
                aria-label={isPlaying ? 'Pause' : 'Abspielen'}
                className="p-3.5 rounded-full bg-primary hover:bg-primary/90 text-white relative z-20 touch-manipulation"
              >
                {isPlaying ? (
                  <Pause className="w-6 h-6" fill="currentColor" />
                ) : (
                  <Play className="w-6 h-6" fill="currentColor" />
                )}
              </button>

              <button
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  skipForward10()
                  saveCurrentProgress()
                }}
                onTouchEnd={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  skipForward10()
                  saveCurrentProgress()
                }}
                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 relative z-20 touch-manipulation"
                title="10 Sekunden vor"
              >
                <RotateCw className="w-5 h-5 text-primary dark:text-white" />
              </button>

              <button
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  goToNextAudio()
                }}
                onTouchEnd={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  goToNextAudio()
                }}
                disabled={!hasNextAudio}
                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-transparent relative z-20 touch-manipulation"
                title="Nächstes Audio"
              >
                <SkipForward className="w-5 h-5 text-primary dark:text-white" fill="currentColor" />
              </button>
            </div>

            <div className="flex justify-end">
              <button
                onClick={(e) => {
                  e.preventDefault()
                  cycleSpeed()
                }}
                className="px-3 py-1 text-sm font-medium rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 dark:bg-primary dark:hover:bg-primary/90 dark:text-white min-w-[50px]"
              >
                {playbackRate}x
              </button>
            </div>
          </div>

          {/* Artikelwechsel im Long-Kapitel: springt an den Anfang des Nachbarartikels,
          unabhängig davon, wo die Pfeile oben gerade in den Audios stehen. */}
          {showArticleNav && (
            <div className="flex items-center justify-between gap-2 px-4 pb-8 -mt-2 relative z-10">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  goToArticle(-1)
                }}
                onTouchEnd={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  goToArticle(-1)
                }}
                disabled={!hasPrevArticle}
                className="flex items-center gap-1 px-3 py-1.5 rounded-full text-sm text-primary dark:text-white hover:bg-gray-100 dark:hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-transparent touch-manipulation"
              >
                <ChevronLeft className="w-4 h-4" />
                Zurück
              </button>
              <span className="text-xs text-gray-500 dark:text-white/75">
                {listPosition
                  ? `${listPosition.index} von ${listPosition.total}`
                  : `${activeArticleIndex + 1} von ${articles.length}`}
              </span>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  goToArticle(1)
                }}
                onTouchEnd={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  goToArticle(1)
                }}
                disabled={!hasNextArticle}
                className="flex items-center gap-1 px-3 py-1.5 rounded-full text-sm text-primary dark:text-white hover:bg-gray-100 dark:hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-transparent touch-manipulation"
              >
                Weiter
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  )

  if (embedded) {
    return playerContent
  }

  return createPortal(playerContent, document.body)
}
